import firebase from 'firebase/compat/app';
import { deleteFlag, deleteFlags } from '../redux/actions/app-actions';
import { FlagType } from '../types/Flag';
import { isAITag, isRetrievalTag } from './tagValidatorUtils';

interface DeleteResponse {
  successMessage?: string;
  errorMessage?: string;
}

export const classifyFlagsForDeletionAndSharing = (
  flags: FlagType[]
): {
  flagIdsToBeDeleted: number[];
  restrictedFlags: FlagType[];
  flagsToBeShared: FlagType[];
} => {
  // List of special flags that cannot be deleted or shared.
  const restrictedFlags = flags.filter(
    (item) => isRetrievalTag(item.tag) || isAITag(item.tag)
  );

  // List of flags that can be shared.
  const flagsToBeShared = flags.filter(
    (item) => !isRetrievalTag(item.tag) || !isAITag(item.tag)
  );

  // List of flag IDs that can be deleted.
  const flagIdsToBeDeleted = flagsToBeShared.map((item) => item.flagId);

  return {
    flagIdsToBeDeleted,
    restrictedFlags,
    flagsToBeShared
  };
};

export const getDeletionConfirmationMessage = (
  flagIdsToBeDeleted: number[],
  restrictedFlags: FlagType[]
): string => {
  if (restrictedFlags.length === 0) {
    return flagIdsToBeDeleted.length === 1
      ? 'Are you sure you want to delete the selected flag?'
      : 'Are you sure you want to delete the selected flags?';
  }

  if (flagIdsToBeDeleted.length === 0) {
    return restrictedFlags.length === 1
      ? 'The selected flag is a system flag and cannot be deleted.'
      : 'The selected flags are system flags and cannot be deleted.';
  }

  return 'Some of the selected flags are system flags and cannot be deleted. Are you sure you want to delete the remaining selected flags?';
};

export const handleDeleteFlags = async (
  selectedFlags: FlagType[]
): Promise<string> => {
  const {
    flagIdsToBeDeleted,
    restrictedFlags
  } = classifyFlagsForDeletionAndSharing(selectedFlags);

  if (restrictedFlags.length > 0 && flagIdsToBeDeleted.length === 0) {
    throw 'SPECIAL_FLAGS';
  }

  try {
    const token = await firebase?.auth()?.currentUser?.getIdToken();

    if (token) {
      let response: DeleteResponse | null | undefined = null;

      if (flagIdsToBeDeleted.length === 1) {
        response = await deleteFlag(token, flagIdsToBeDeleted[0]);
      } else {
        response = await deleteFlags(token, flagIdsToBeDeleted);
      }

      if (response?.successMessage) {
        return token;
      } else if (response?.errorMessage) {
        throw new Error(response.errorMessage);
      }
    }

    throw new Error('Failed to retrieve authentication token.');
  } catch (error) {
    console.error(error);
    throw error;
  }
};
